<template>
  <v-container fluid>
    <v-row justify="space-between" class="px-10">
      <v-col cols="12" sm="12" md="7" lg="6">
        <v-btn
          color="primary"
          icon
          size="20"
          class="text-center"
          @click="$router.go(-1)"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <p class="text-h6 text--secondary d-inline font-weight-bold">
          AI Chat Configuration
        </p>
      </v-col>
    </v-row>

    <v-row class="px-10 mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title>AI Chat Configuration</v-card-title>
          <v-card-subtitle>Enable or disable the AI Chat feature and configure access permissions</v-card-subtitle>
          <v-card-text>
            <v-row>
              <v-col cols="12" class="d-flex align-center">
                <span class="mr-3 text--secondary">Enable AI Chat</span>
                <v-switch
                  v-model="config.enabled"
                  color="primary"
                  hide-details
                  :disabled="loading"
                  style="margin-top: 0;"
                ></v-switch>
              </v-col>
              <v-col cols="12" v-if="!config.enabled">
                <v-alert type="warning" dense outlined>
                  AI Chat is currently disabled. When disabled, no users can access the AI chat feature.
                </v-alert>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="d-flex align-center">
                <span class="mr-3 text--secondary">Enable Requests Limit</span>
                <v-switch
                  v-model="config.requestsLimitEnabled"
                  color="primary"
                  hide-details
                  :disabled="loading"
                  style="margin-top: 0;"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="6" v-if="config.requestsLimitEnabled">
                <v-text-field
                  v-model="config.requestsLimitValue"
                  label="Requests Limit"
                  placeholder="Enter the maximum number of requests"
                  hint="Maximum number of requests allowed"
                  persistent-hint
                  :disabled="loading"
                  outlined
                  dense
                  type="number"
                  min="1"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <h3 class="mb-3">AI Endpoint Configuration</h3>
                <v-text-field
                  v-model="config.endpoint"
                  label="AI Endpoint URL"
                  placeholder="https://api.example.com/ai/chat"
                  hint="Enter the endpoint URL to get responses from the AI"
                  persistent-hint
                  :disabled="loading"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <h3 class="mb-3">Access Permissions</h3>
                <v-alert type="info" dense outlined class="mb-3">
                  Configure who can access the AI Chat when it's enabled. These settings only apply when AI Chat is enabled. You can configure permissions even when the chat is disabled.
                </v-alert>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="d-flex align-center">
                      <span class="mr-3 text--secondary">Grant Access to All Users</span>
                      <v-switch
                        v-model="config.allUsers"
                        color="primary"
                        hide-details
                        :disabled="loading"
                        style="margin-top: 0;"
                      ></v-switch>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="d-flex align-center">
                      <span class="mr-3 text--secondary">Grant Access to All Premium Users</span>
                      <v-switch
                        v-model="config.allPremiumUsers"
                        color="primary"
                        hide-details
                        :disabled="loading"
                        style="margin-top: 0;"
                      ></v-switch>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>


            <v-row>
              <v-col cols="12">
                <h3 class="mb-3">Individual User Selection</h3>
                <v-autocomplete
                  v-model="selectedUser"
                  :items="filteredUsers"
                  :search-input.sync="userSearch"
                  item-text="displayName"
                  item-value="id"
                  label="Search and select a user"
                  placeholder="Type to search users..."
                  return-object
                  :disabled="loading"
                  clearable
                  no-filter
                  @change="onUserSelected"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.displayName || item.firstName + ' ' + item.lastName }}
                        <v-chip
                          v-if="userHasPermission(item.id, item)"
                          x-small
                          color="success"
                          class="ml-2"
                        >
                          <v-icon x-small left>mdi-check-circle</v-icon>
                          Has Access
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip
                        v-if="item.activeSubscription?.isPremium || item.isPremium"
                        small
                        color="primary"
                        class="mr-1"
                      >
                        Premium
                      </v-chip>
                    </v-list-item-action>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <h3 class="mb-3">Current Access Permissions</h3>
                <v-card outlined>
                  <v-card-text>
                    <div v-if="!config.enabled" class="mb-3">
                      <v-alert type="warning" dense outlined>
                        AI Chat is currently disabled. The permissions below are configured but will not take effect until AI Chat is enabled.
                      </v-alert>
                    </div>
                    <div v-if="config.allUsers" class="mb-2">
                      <v-chip color="success" class="mr-2">
                        <v-icon left small>mdi-check</v-icon>
                        All Users have access
                      </v-chip>
                    </div>
                    <div v-if="config.allPremiumUsers" class="mb-2">
                      <v-chip color="primary" class="mr-2">
                        <v-icon left small>mdi-check</v-icon>
                        All Premium Users have access
                      </v-chip>
                    </div>
                    <div v-if="config.specificUserIds && config.specificUserIds.length > 0" class="mb-2">
                      <div class="mb-1">
                        <strong>Individual Users with access ({{ config.specificUserIds.length }}):</strong>
                      </div>
                      <div>
                        <v-chip
                          v-for="userId in config.specificUserIds"
                          :key="userId"
                          color="info"
                          class="mr-2 mb-2"
                          close
                          @click:close="removeUser(userId)"
                        >
                          {{ getUserDisplayName(userId) }}
                        </v-chip>
                      </div>
                    </div>
                    <div v-if="!config.allUsers && !config.allPremiumUsers && (!config.specificUserIds || config.specificUserIds.length === 0)">
                      <span class="text--secondary">No access permissions granted. Configure permissions above.</span>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12" class="text-right">
                <v-btn
                  color="success"
                  :loading="loading"
                  :disabled="loading"
                  @click="saveConfiguration"
                >
                  Save Configuration
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { doc, setDoc, getDoc } from "firebase/firestore";
import { getDb } from "@/firebase";
import algoliasearch from "algoliasearch/lite";

export default {
  name: "AiPermissions",
  components: {},
  data: () => ({
    loading: false,
    config: {
      // enabled: Controls whether AI Chat feature is available at all
      // When false, no users can access AI Chat regardless of permission settings
      // When true, permission settings (allUsers, allPremiumUsers, specificUserIds) determine access
      enabled: false,
      endpoint: '',
      requestsLimitEnabled: false,
      requestsLimitValue: null,
      allUsers: false,
      allPremiumUsers: false,
      specificUserIds: [],
      updatedAt: null,
    },
    users: [],
    userSearch: "",
    selectedUser: null,
    searchTimeout: null,
    userCache: {}, // Cache for user data by ID
    searchClient: algoliasearch(
      "SCII59RM36",
      "f935a55998c09178ae15d9d9d7a570a7"
    ),
    snackbar: {
      show: false,
      text: "",
      color: "success",
      timeout: 3000,
    },
  }),
  computed: {
    filteredUsers() {
      return this.users;
    },
  },
  watch: {
    userSearch: {
      handler(newVal) {
        if (newVal && newVal.length >= 2) {
          this.onSearchInput(newVal);
        } else {
          this.users = [];
        }
      },
      immediate: false,
    },
  },
  async created() {
    this.loading = true;
    try {
      await this.loadConfiguration();
      await this.loadUsers();
      // Load user details after configuration is loaded
      if (this.config.specificUserIds && this.config.specificUserIds.length > 0) {
        await this.loadUserDetailsForIds();
      }
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadConfiguration() {
      this.loading = true;
      try {
        const db = getDb();
        const aiPermissionsRef = doc(db, "appConfiguration", "aiPermissions");
        const docSnap = await getDoc(aiPermissionsRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          this.config = {
            enabled: data.enabled || false,
            endpoint: data.endpoint || '',
            requestsLimitEnabled: data.requestsLimitEnabled || false,
            requestsLimitValue: data.requestsLimitValue || null,
            allUsers: data.allUsers || false,
            allPremiumUsers: data.allPremiumUsers || false,
            specificUserIds: data.specificUserIds || [],
            updatedAt: data.updatedAt || null,
          };
        } else {
          // Initialize with default values
          this.config = {
            enabled: false,
            endpoint: '',
            requestsLimitEnabled: false,
            requestsLimitValue: null,
            allUsers: false,
            allPremiumUsers: false,
            specificUserIds: [],
            updatedAt: null,
          };
        }
      } catch (error) {
        console.error("Error loading configuration:", error);
        this.showSnackbar("Error loading configuration", "error");
      } finally {
        this.loading = false;
      }
    },
    async loadUsers() {
      // Users will be loaded on-demand when searching
      // This is more efficient for large user bases
      this.users = [];
    },
    async loadUserDetailsForIds() {
      if (!this.config.specificUserIds || this.config.specificUserIds.length === 0) {
        return;
      }
      
      try {
        const db = getDb();
        const userIds = this.config.specificUserIds;
        
        // Load user details in parallel for better performance
        const userPromises = userIds.map(async (userId) => {
          if (!this.userCache[userId]) {
            try {
              const userRef = doc(db, "users", userId);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                // Use Vue.set to ensure reactivity
                this.$set(this.userCache, userId, {
                  id: userSnap.id,
                  ...userSnap.data(),
                });
              } else {
                // User doesn't exist, cache with minimal info
                this.$set(this.userCache, userId, {
                  id: userId,
                  displayName: userId,
                  email: '',
                });
              }
            } catch (error) {
              console.error(`Error loading user ${userId}:`, error);
              // Cache with minimal info on error
              this.$set(this.userCache, userId, {
                id: userId,
                displayName: userId,
                email: '',
              });
            }
          }
        });
        
        await Promise.all(userPromises);
      } catch (error) {
        console.error("Error loading user details:", error);
      }
    },
    async onSearchInput(searchTerm) {
      if (!searchTerm || searchTerm.length < 2) {
        this.users = [];
        return;
      }
      
      // Debounce the search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(async () => {
        this.users = await this.searchUsers(searchTerm);
      }, 300);
    },
    async searchUsers(searchTerm) {
      if (!searchTerm || searchTerm.length < 2) {
        return [];
      }
      
      try {
        // Use Algolia to search users, same as user-list view
        const response = await this.searchClient.search([
          {
            indexName: "usersIndex",
            query: searchTerm,
            params: {
              hitsPerPage: 50,
            },
          },
        ]);
        
        // The response structure is: { results: [...] }
        const results = response.results || [];
        
        if (results.length > 0 && results[0].hits) {
          return results[0].hits.map(hit => {
            // Algolia uses objectID as the unique identifier, which should be the userId
            const userId = hit.objectID || hit.userId || hit.id;
            return {
              id: userId,
              displayName: hit.displayName || "",
              firstName: hit.firstName || "",
              lastName: hit.lastName || "",
              email: hit.email || "",
              activeSubscription: hit.activeSubscription || null,
              isPremium: hit.isPremium || false,
              ...hit,
            };
          });
        }
        
        return [];
      } catch (error) {
        console.error("Error searching users with Algolia:", error);
        return [];
      }
    },
    async saveConfiguration() {
      this.loading = true;
      try {
        const db = getDb();
        const aiPermissionsRef = doc(db, "appConfiguration", "aiPermissions");
        
        const configToSave = {
          ...this.config,
          updatedAt: new Date(),
        };
        
        await setDoc(aiPermissionsRef, configToSave, { merge: true });
        this.showSnackbar("Configuration saved successfully", "success");
      } catch (error) {
        console.error("Error saving configuration:", error);
        this.showSnackbar("Error saving configuration", "error");
      } finally {
        this.loading = false;
      }
    },
    onUserSelected(user) {
      if (user && user.id) {
        // Check if user already has permission
        if (this.userHasPermission(user.id, user)) {
          this.showSnackbar("User already has access", "info");
          this.selectedUser = null;
          this.userSearch = "";
          return;
        }
        
        // Add user to specific users list
        if (!this.config.specificUserIds.includes(user.id)) {
          this.config.specificUserIds.push(user.id);
          // Cache the user data
          this.userCache[user.id] = user;
          this.showSnackbar(`User added. Don't forget to save configuration.`, "info");
        } else {
          this.showSnackbar("User already has permission", "info");
        }
        this.selectedUser = null;
        this.userSearch = "";
      }
    },
    removeUser(userId) {
      const index = this.config.specificUserIds.indexOf(userId);
      if (index > -1) {
        this.config.specificUserIds.splice(index, 1);
        this.showSnackbar("User removed. Don't forget to save configuration.", "info");
      }
    },
    userHasPermission(userId, userItem = null) {
      // Check if user has permission through any method
      if (this.config.allUsers) {
        return true;
      }
      if (this.config.allPremiumUsers) {
        // Check if user is premium (from the item or cache)
        if (userItem && (userItem.isPremium || userItem.activeSubscription?.isPremium)) {
          return true;
        }
        // Check cache if available
        const cachedUser = this.userCache[userId];
        if (cachedUser && (cachedUser.isPremium || cachedUser.activeSubscription?.isPremium)) {
          return true;
        }
      }
      if (this.config.specificUserIds && this.config.specificUserIds.includes(userId)) {
        return true;
      }
      return false;
    },
    getUserDisplayName(userId) {
      // First check cache
      const cachedUser = this.userCache[userId];
      if (cachedUser) {
        const displayName = cachedUser.displayName || 
          `${cachedUser.firstName || ''} ${cachedUser.lastName || ''}`.trim() || 
          cachedUser.email;
        if (displayName) {
          return displayName;
        }
      }
      
      // Then check current users list
      const user = this.users.find(u => u.id === userId);
      if (user) {
        const displayName = user.displayName || 
          `${user.firstName || ''} ${user.lastName || ''}`.trim() || 
          user.email;
        if (displayName) {
          return displayName;
        }
      }
      
      // Fallback to userId if name not found
      return userId;
    },
    showSnackbar(text, color = "success") {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout: 3000,
      };
    },
  },
};
</script>

<style scoped>
</style>
